import { useForm } from 'react-hook-form'
import styles from './styles.module.css'
import { ArrowRightIcon } from 'components/design-system/kickoff-icons-v2/arrow-right-icon'
import { useMutation } from '@apollo/client'
import { addToWaitlist as addToWaitlistMutation } from './mutations.graphql'
import { useEffect } from 'react'

interface FormData {
  phoneNumber: string
  optInTextMessages: boolean
}

const formatPhoneNumber = (value: string) => {
  // Remove all non-numeric characters
  let numbers = value.replace(/\D/g, '')

  // Remove country code if it starts with "1" and has 11 digits
  if (numbers.length === 11 && numbers.startsWith('1')) {
    numbers = numbers.slice(1)
  }

  // Format the number as (XXX) XXX-XXXX
  if (numbers.length <= 3) {
    return numbers
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`
  } else {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
      6,
      10
    )}`
  }
}

export const WaitlistPhoneNumberForm = ({
  clientId,
  phoneNumber,
  onSubmit,
}: {
  clientId: string
  phoneNumber?: string
  onSubmit: () => void
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>()

  const [addToWaitlist] = useMutation(addToWaitlistMutation)

  // Prepopulate form with existing phone number
  useEffect(() => {
    if (phoneNumber) {
      const formatted = formatPhoneNumber(phoneNumber)
      setValue('phoneNumber', formatted)
    }
  }, [phoneNumber, setValue])

  const optInTextMessages = watch('optInTextMessages')

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setValue('phoneNumber', formatted)
  }

  const validatePhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.startsWith('1')) {
      return 'Please enter a valid phone number without the country code'
    }
    if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(value)) {
      return 'Please enter a valid phone number'
    }
    return true
  }

  const handleFormSubmit = async (data: FormData) => {
    await addToWaitlist({
      variables: { id: clientId, phoneNumber: data.phoneNumber },
    })
    onSubmit()
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.form}>
      <div className={styles.formGroup}>
        <label className={styles.label}>
          Phone Number<span className={styles.required}>*</span>
        </label>
        <input
          type="tel"
          {...register('phoneNumber', {
            required: 'Phone number is required',
            validate: validatePhoneNumber,
          })}
          className={styles.input}
          placeholder="(555) 555-5555"
          onChange={handlePhoneChange}
          maxLength={14}
        />
        {errors.phoneNumber && (
          <span className={styles.error}>{errors.phoneNumber.message}</span>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            {...register('optInTextMessages')}
            className={styles.checkbox}
          />
          <span className={styles.checkboxCustom}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="9"
              viewBox="0 0 10 9"
              fill="none">
              <path
                d="M1.5 5L3.5 7L8.5 2"
                stroke="#030017"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Opt-in to receive text messages
        </label>
      </div>

      <button
        type="submit"
        className={styles.button}
        disabled={!optInTextMessages}
        style={{ opacity: optInTextMessages ? 1 : 0.4 }}>
        Submit <ArrowRightIcon strokeWidth={2} width={13} height={7} />
      </button>
    </form>
  )
}
